import { ShadowContainer } from '../../../common/ShadowContainer';
import { TEXTS } from '../../../../core/texts';
import { Button, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { ColumnBoxFlexFullWidth, FlexBoxFullWidth } from '../../../common/boxes';
import { ChangeEvent, useCallback, useContext, useMemo, useState } from 'react';
import { ValidationErrorState } from '../../../../types/validationErrorState';
import { AVAILABILITY_TYPE_MAP, AvailabilityType } from '../../../../enums/AvailabilityType';
import { IAvailability } from '../../../../interfaces/availability';
import { PREFERENCE_TIME_MAP, ProjectPreferenceTime } from '../../../../enums/projectPreferenceTime';
import { CURRENCY_CODES } from '../../../../core/currencies';
import { PaymentRateType } from '../../../../enums/paymentRateType';
import dayjs from 'dayjs';
import TimeSelectAndTimezone from '../../../common/TimeSelectAndTimezone';
import NextProjectDatePicker from './NextProjectDatePicker';
import RateAvailability from '../../../common/RateAvailability';
import { ButtonWithLoader } from '../../../common/ButtonWithLoader';
import { Validator } from '../../../../core/Validator';
import { Add, Edit } from '@mui/icons-material';
import { FreelancerContext } from '../FreelancerEntity';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FreelancerService } from '../../../../services/freelancer';
import { useNotification } from '../../../../+state/useNotification';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../../../interfaces/errorResponse';
import { BorderBoxWithTitle } from '../../../common/BorderBoxWithTitle';
import { FREELANCER_ENTITY_QUERY_KEY } from '../../../../queryHooks/freelancer/useGetFreelancerEntity';
import { parseDate } from '../../../../utils/parseDate';

const initialEntity: IAvailability = {
  type: AvailabilityType.FULL_TIME,
  weeklyHoursAvailable: 40,
  projectPreferenceType: ProjectPreferenceTime.LONG_TERM,
  nextProjectStart: parseDate(dayjs()),
  fromHour: 10,
  toHour: 18,
  timezone: '',
  rate: {
    amount: 1,
    currency: CURRENCY_CODES[0], //EUR
    type: PaymentRateType.HOURLY,
  },
  description: '',
  expectedAbsences: ''
};

const Availability = () => {
  const { user } = useContext(FreelancerContext);
  const { openSuccessToast, openErrorToast } = useNotification();
  const queryClient = useQueryClient();
  const existingEntity = useMemo(() => user.freelancerProfile.availability, [user.freelancerProfile]);
  const [entity, setEntity] = useState<IAvailability>(existingEntity || initialEntity);
  const [editMode, setEditMode] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorState>({});

  const mutation = useMutation({
    mutationFn: (availability: IAvailability) => FreelancerService.saveAvailability(user.id, availability),
    onSuccess: () => {
      openSuccessToast(TEXTS.SUCCESS_SAVED);
      queryClient.invalidateQueries([FREELANCER_ENTITY_QUERY_KEY]);
    },
    onError: (err: AxiosError<IErrorResponse>) => {
      openErrorToast(err.response?.data.message || err.message);
    }
  });

  const toggleEditMode = useCallback(() => {
    setEditMode(prev => !prev);
    setEntity(existingEntity || initialEntity);
    setValidationErrors({});
  }, [existingEntity]);

  const hasChanges = useMemo(() => {
    return !existingEntity || (
      !!Object.keys(entity).filter(k => k !== 'rate').find((key) => (entity as any)[key] !== (existingEntity as any)?.[key])
      || Object.keys(entity.rate).find(key => (entity.rate as any)[key] !== (existingEntity?.rate as any)?.[key])
    );
  }, [entity, existingEntity]);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEntity(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleNumberInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (isNaN(+value) || +value < 0) {return;}
    setEntity(prev => ({ ...prev, [name]: value !== '' ? +value : value }));
  }, []);

  const handleDateChange = useCallback((date: string) => {
    setEntity(prev => ({ ...prev, nextProjectStart: date }));
  }, []);

  const handleRateInputChanges = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value && !isNaN(+value) && +value < 0) {return;}
    setEntity(prev => ({ ...prev, rate: { ...prev.rate, [name]: value && !isNaN(+value) ? +value : value } }));
  }, []);

  const onSubmit = useCallback((e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { rate, ...values } = entity;
    const validationErr = Validator.validate({
      ...values,
      rateAmount: rate.amount,
      rateCurrency: rate.currency,
      rateType: rate.type
    }, Validator.NAMES.AVAILABILITY);
    if (validationErr) {
      return setValidationErrors(validationErr);
    }
    mutation.mutate(entity);
    setValidationErrors({});
    setEditMode(false);
  }, [entity, mutation]);

  return (
    <ShadowContainer maxWidth={800} alignSelf={'center'}>
      <FlexBoxFullWidth justifyContent={'space-between'} mb={3}>
        <Typography variant={'h3'}>
          {TEXTS.AVAILABILITY}
        </Typography>
        {
          !editMode &&
          <Tooltip title={user.freelancerProfile.availability ? TEXTS.EDIT : TEXTS.ADD}>
            <IconButton onClick={toggleEditMode} color={'primary'}>
              {user.freelancerProfile.availability ? <Edit/> : <Add/>}
            </IconButton>
          </Tooltip>

        }
      </FlexBoxFullWidth>
      {
        (editMode || user.freelancerProfile.availability) ?
          <ColumnBoxFlexFullWidth>
            <form onSubmit={onSubmit}>
              <FlexBoxFullWidth my={2} justifyContent={'space-between'} flexDirection={{
                mobile: 'column',
                laptop: 'row'
              }}>
                <TextField name={'type'} label={TEXTS.TYPE} value={entity.type} select={true}
                           required={editMode && !!Validator.LENGTHS.AVAILABILITY['type']?.min}
                           error={!!validationErrors['type']} helperText={validationErrors['type']}
                           disabled={!editMode} onChange={handleInputChange}
                           variant={editMode ? 'outlined' : 'standard'}
                           sx={{ width: { mobile: '100%', laptop: 180 } }}
                >
                  {
                    Object.values(AvailabilityType).map(type => (
                      <MenuItem key={type} value={type}>
                        {AVAILABILITY_TYPE_MAP[type]}
                      </MenuItem>
                    ))
                  }
                </TextField>
                <TextField name={'weeklyHoursAvailable'}
                           label={TEXTS.WEEKLY_HOURS_AVAILABLE}
                           required={editMode && !!Validator.LENGTHS.AVAILABILITY['weeklyHoursAvailable']?.min}
                           value={entity.weeklyHoursAvailable}
                           onChange={handleNumberInputChange}
                           error={!!validationErrors['weeklyHoursAvailable']}
                           helperText={validationErrors['weeklyHoursAvailable']}
                           type={'number'} disabled={!editMode} variant={editMode ? 'outlined' : 'standard'}
                           sx={{ width: { mobile: '100%', laptop: 180 }, mt: { mobile: 4, laptop: 0 } }}
                />

                <TextField name={'projectPreferenceType'} label={TEXTS.PROJECT_PREFERENCE_TYPE}
                           required={editMode && !!Validator.LENGTHS.AVAILABILITY['projectPreferenceType']?.min}
                           value={entity.projectPreferenceType}
                           disabled={!editMode} variant={editMode ? 'outlined' : 'standard'}
                           onChange={handleInputChange} error={!!validationErrors['projectPreferenceType']}
                           helperText={validationErrors['projectPreferenceType']} select
                           sx={{ width: { mobile: '100%', laptop: 180 }, mt: { mobile: 4, laptop: 0 } }}
                >
                  {
                    Object.values(ProjectPreferenceTime).map(pref => (
                      <MenuItem key={pref} value={pref}>
                        {PREFERENCE_TIME_MAP[pref]}
                      </MenuItem>
                    ))
                  }
                </TextField>
              </FlexBoxFullWidth>

              <FlexBoxFullWidth mt={4} justifyContent={'center'}>
                <NextProjectDatePicker handleDateChange={handleDateChange} nextProjectStart={entity.nextProjectStart}
                                       validationErrors={validationErrors} disabled={!editMode}
                />
              </FlexBoxFullWidth>

              <TimeSelectAndTimezone fromHour={entity.fromHour} toHour={entity.toHour} timezone={entity.timezone}
                                     validationErrors={validationErrors} handleInputChange={handleInputChange}
                                     disabled={!editMode}
              />
              <BorderBoxWithTitle title={TEXTS.RATE} mb={3} py={3} borderStyle={editMode ? 'solid' : 'dotted'}>
                <RateAvailability rateAmount={entity.rate.amount} handleInputChange={handleRateInputChanges}
                                  rateCurrency={entity.rate.currency} rateType={entity.rate.type}
                                  validationErrors={validationErrors} disabled={!editMode}
                />
              </BorderBoxWithTitle>
              <TextField name={'description'} value={entity.description} multiline onChange={handleInputChange}
                         label={TEXTS.DESCRIPTION} fullWidth
                         required={editMode && !!Validator.LENGTHS.AVAILABILITY['description']?.min}
                         sx={{ fontStyle: 'italic' }} disabled={!editMode} variant={editMode ? 'outlined' : 'standard'}
              />
              <TextField name={'expectedAbsences'} value={entity.expectedAbsences} multiline onChange={handleInputChange}
                         label={TEXTS.EXPECTED_ABSENCES} fullWidth
                         required={editMode && !!Validator.LENGTHS.AVAILABILITY['expectedAbsences']?.min}
                         sx={{ fontStyle: 'italic', mt: 2 }} disabled={!editMode} variant={editMode ? 'outlined' : 'standard'}
              />
              {
                editMode &&
                <FlexBoxFullWidth justifyContent={'space-between'} mt={4}>
                  <Button variant={'outlined'} color={'error'} onClick={toggleEditMode}>{TEXTS.CANCEL}</Button>
                  <ButtonWithLoader variant={'outlined'} type={'submit'} disabled={!hasChanges}
                                    loading={mutation.isLoading}>{TEXTS.SAVE}</ButtonWithLoader>
                </FlexBoxFullWidth>
              }
            </form>
          </ColumnBoxFlexFullWidth>
          : <Typography>
            {TEXTS.NOT_SET}
          </Typography>
      }
    </ShadowContainer>
  );
};

export default Availability;